/**
 * `<caos-result-grid>` — the rows a query came back with: sorted, paged and exportable, in hand.
 * (Catalog: Developer surfaces › ResultGrid — a complete query result set, sorted and paged in the browser, with record ids that open.)
 *
 * WHY THIS IS NOT `<caos-data-table>` WITH A FOOTER. The table deliberately does not sort its own
 * rows: a list surface holds one page of a set that lives on the server, so the only correct answer
 * to a header press is to ask the server for a differently-ordered page, and a table that reordered
 * the page it was holding would order thirty of four thousand records and look like it had worked.
 *
 * A query result is the opposite situation and wants the opposite behaviour. The whole set is
 * already here — it is what the query returned — so sorting it is a local fact, and asking the
 * application to re-run somebody's query to change a column order would be both slow and wrong
 * (re-running can return different rows). So this component owns its ordering and its paging, over
 * the rows it was handed, and composes `<caos-data-table>` for the drawing. Two components, one
 * table, two genuinely different answers about who owns the order.
 *
 * A RECORD ID IS A DESTINATION, not a string. Name a column in `id-columns` and its cells become
 * real buttons raising `caos-result-open-record`; the application opens it wherever records open.
 * The component neither knows nor invents a URL, which is what keeps it usable by an application
 * whose records live somewhere this one has never heard of.
 *
 * SORTING PUTS EMPTIES LAST in both directions. A null is not smaller than every value, it is the
 * absence of one, and floating them to the top of a descending sort buries the rows somebody
 * actually asked to see. Ascending and descending both mean "the rows with something in this
 * column, ordered" followed by the rows without.
 *
 * Styling seams: `::part(grid)`, `::part(summary)`, `::part(table)`, `::part(footer)`,
 * `::part(pager)`, `::part(page-status)`, `::part(export)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosTableColumn, CaosTableRow, CaosSortDirection } from './data-table.js';

/** One column of a result. The same shape the table takes, so nothing is transliterated. */
export type CaosResultColumn = CaosTableColumn;

/** One row of a result: values keyed by column key. */
export interface CaosResultRow {
  readonly [key: string]: string | number | boolean | null | undefined;
}

export const RESULT_PAGE_SIZE_DEFAULT = 50;

const sheet = makeSheet(`
  :host { display: block; }

  .grid { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--caos-space-3);
    flex-wrap: wrap;
  }

  .pager { display: inline-flex; align-items: center; gap: var(--caos-space-2); }

  .pager button, .export {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 4px 10px;
    cursor: pointer;
  }
  .pager button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .pager button:focus-visible, .export:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .page-status {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
    font-variant-numeric: tabular-nums;
  }

`);

/**
 * Order rows by one column, EMPTIES LAST in both directions. See the header for why that is not a
 * detail: a null is the absence of a value rather than a small one.
 */
export function orderResultRows(
  rows: readonly CaosResultRow[],
  key: string,
  direction: CaosSortDirection,
): readonly CaosResultRow[] {
  const empty = (v: unknown): boolean => v === null || v === undefined || v === '';
  const sign = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (empty(x) && empty(y)) return 0;
    if (empty(x)) return 1;
    if (empty(y)) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign;
  });
}

export class CaosResultGrid extends CaosElement {
  static get observedAttributes(): string[] {
    return ['page', 'page-size', 'id-columns', 'took-ms', 'export-label', 'truncated'];
  }

  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;
  private prevEl: HTMLButtonElement;
  private nextEl: HTMLButtonElement;
  private pageStatusEl: HTMLSpanElement;
  private exportEl: HTMLButtonElement;

  private cols: readonly CaosResultColumn[] = [];
  private data: readonly CaosResultRow[] = [];
  private order: { key: string; direction: CaosSortDirection } | null = null;

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'grid';
    wrap.setAttribute('part', 'grid');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      // The table told us a header was pressed. The ordering is ours, so it stops here rather than
      // reaching the application, which would otherwise see a sort request it must not act on.
      e.stopPropagation();
      this.order = { key: column, direction };
      this.setAttribute('page', '1');
      this.render();
      this.dispatchEvent(
        new CustomEvent('caos-result-sort', { bubbles: true, composed: true, detail: { column, direction } }),
      );
    });

    const footer = document.createElement('div');
    footer.className = 'footer';
    footer.setAttribute('part', 'footer');

    const pager = document.createElement('div');
    pager.className = 'pager';
    pager.setAttribute('part', 'pager');
    this.prevEl = document.createElement('button');
    this.prevEl.type = 'button';
    this.prevEl.textContent = 'Previous';
    this.prevEl.addEventListener('click', () => this.goTo(this.page - 1));
    this.nextEl = document.createElement('button');
    this.nextEl.type = 'button';
    this.nextEl.textContent = 'Next';
    this.nextEl.addEventListener('click', () => this.goTo(this.page + 1));
    this.pageStatusEl = document.createElement('span');
    this.pageStatusEl.className = 'page-status';
    this.pageStatusEl.setAttribute('part', 'page-status');
    this.pageStatusEl.setAttribute('role', 'status');
    pager.append(this.prevEl, this.pageStatusEl, this.nextEl);

    this.exportEl = document.createElement('button');
    this.exportEl.type = 'button';
    this.exportEl.className = 'export';
    this.exportEl.setAttribute('part', 'export');
    this.exportEl.addEventListener('click', () => this.exportRows());

    footer.append(pager, this.exportEl);
    wrap.append(this.summaryEl, this.tableEl, footer);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get columns(): readonly CaosResultColumn[] {
    return this.cols;
  }

  set columns(next: readonly CaosResultColumn[]) {
    this.cols = Array.isArray(next) ? next : [];
    this.render();
  }

  get rows(): readonly CaosResultRow[] {
    return this.data;
  }

  set rows(next: readonly CaosResultRow[]) {
    this.data = Array.isArray(next) ? next : [];
    // A new result set is a new result set: staying on page 7 of the previous one would show
    // somebody an empty grid and let them conclude their query returned nothing.
    this.order = null;
    this.setAttribute('page', '1');
    this.render();
  }

  get pageSize(): number {
    const n = Number(this.getAttribute('page-size'));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : RESULT_PAGE_SIZE_DEFAULT;
  }

  /** How many pages the result set makes. A result with no rows is one (empty) page, never zero. */
  get pageCount(): number {
    return Math.max(1, Math.ceil(this.data.length / this.pageSize));
  }

  /** The page on screen, 1-based, clamped into the set that exists. */
  get page(): number {
    const n = Number(this.getAttribute('page'));
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(Math.floor(n), this.pageCount);
  }

  set page(n: number) {
    this.setAttribute('page', String(n));
  }

  /** The whole result set in the order it is being shown — every page, not the one on screen. */
  get ordered(): readonly CaosResultRow[] {
    return this.order ? orderResultRows(this.data, this.order.key, this.order.direction) : this.data;
  }

  /** Just the rows on screen. */
  get visibleRows(): readonly CaosResultRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.ordered.slice(start, start + this.pageSize);
  }

  /** Which columns hold record ids, from the `id-columns` attribute. */
  private get idColumns(): Set<string> {
    return new Set(
      (this.getAttribute('id-columns') ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== ''),
    );
  }

  private goTo(n: number): void {
    const next = Math.min(Math.max(1, n), this.pageCount);
    if (next === this.page) return;
    this.setAttribute('page', String(next));
    this.dispatchEvent(
      new CustomEvent('caos-result-page', {
        bubbles: true,
        composed: true,
        detail: { page: next, pageCount: this.pageCount },
      }),
    );
  }

  private exportRows(): void {
    // The whole set in the order shown, not the page — somebody exporting a result wants the
    // result. Writing the file is the application's, because a component library cannot know
    // whether this page may start a download.
    this.dispatchEvent(
      new CustomEvent('caos-result-export', {
        bubbles: true,
        composed: true,
        detail: { columns: this.cols, rows: this.ordered },
      }),
    );
  }

  private openRecord(id: string, row: CaosResultRow): void {
    this.dispatchEvent(new CustomEvent('caos-result-open-record', { bubbles: true, composed: true, detail: { id, row } }));
  }

  private render(): void {
    this.tableEl.columns = this.cols.map((c) => ({ ...c, sortable: c.sortable !== false }));
    this.tableEl.sort = this.order;
    this.tableEl.rows = this.visibleRows.map((row, i) => this.toTableRow(row, i));

    const total = this.data.length;
    const took = this.getAttribute('took-ms');
    const parts: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent = `${total.toLocaleString()} ${total === 1 ? 'row' : 'rows'}`;
    parts.push(strong);
    if (this.hasAttribute('truncated')) {
      // A truncated result is a different claim from a complete one, and a grid that says "2,000
      // rows" over a capped set tells somebody their query matched exactly two thousand things.
      parts.push(document.createTextNode(' (truncated — more matched than were returned)'));
    }
    if (took !== null && took !== '') parts.push(document.createTextNode(` · ran in ${took} ms`));
    this.summaryEl.replaceChildren(...parts);

    this.pageStatusEl.textContent = `Page ${this.page} of ${this.pageCount}`;
    this.prevEl.disabled = this.page <= 1;
    this.nextEl.disabled = this.page >= this.pageCount;
    this.exportEl.textContent = this.getAttribute('export-label') ?? 'Export';
    this.exportEl.disabled = total === 0;
  }

  private toTableRow(row: CaosResultRow, index: number): CaosTableRow {
    const ids = this.idColumns;
    const cells: Record<string, unknown> = {};
    for (const col of this.cols) {
      const raw = row[col.key];
      if (ids.has(col.key) && raw !== null && raw !== undefined && String(raw) !== '') {
        const b = document.createElement('button');
        b.type = 'button';
        // Styled by the TABLE's own sheet, not this one: the button is appended into the table's
        // shadow root, which this component's stylesheet cannot reach. See the rule beside the
        // cell-link rules in `data-table.ts`.
        b.className = 'caos-table__idlink';
        b.textContent = String(raw);
        b.setAttribute('aria-label', `Open record ${String(raw)}`);
        b.addEventListener('click', () => this.openRecord(String(raw), row));
        cells[col.key] = { node: b };
      } else if (raw === null || raw === undefined) {
        // An empty cell is empty. Writing "null" into it would be showing a person the shape of the
        // transport rather than the state of their data.
        cells[col.key] = '';
      } else {
        cells[col.key] = typeof raw === 'boolean' ? (raw ? 'true' : 'false') : raw;
      }
    }
    const first = this.cols.find((c) => ids.has(c.key));
    const id = first ? String(row[first.key] ?? index) : String(index);
    return { id, cells } as CaosTableRow;
  }
}

define('caos-result-grid', CaosResultGrid);

declare global {
  interface HTMLElementTagNameMap {
    'caos-result-grid': CaosResultGrid;
  }
}
